import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../index.css';

const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get('/api/partners');
      setPartners(response.data);
    } catch (err) {
      console.error('Error fetching partners:', err);
      setError('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div className="loading">Loading partners...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p style={{ color: 'var(--error)' }}>{error}</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
      {/* Header Section */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '60px',
        animation: 'fadeInUp 0.6s ease-out'
      }}>
        <h1 style={{
          fontSize: '48px',
          fontWeight: '800',
          marginBottom: '20px',
          textTransform: 'uppercase',
          letterSpacing: '2px',
          background: 'linear-gradient(135deg, #8a2be2, #00bfff)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          display: 'inline-block'
        }}>
          Our Partners
        </h1>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '18px',
          lineHeight: '1.6',
          maxWidth: '700px',
          margin: '0 auto'
        }}>
          We're proud to collaborate with these amazing brands that share our vision for gaming excellence
        </p>
      </div>

      {/* Partners Grid */}
      {partners.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: 'var(--card-bg)',
          borderRadius: '16px',
          border: '1px solid var(--border)'
        }}>
          <p style={{ 
            color: 'var(--text-secondary)', 
            fontSize: '18px' 
          }}>
            No partners to display at the moment
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: '30px',
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          {partners.map((partner, index) => (
            <div
              key={partner._id}
              style={{
                backgroundColor: 'var(--card-bg)',
                borderRadius: '16px',
                padding: '30px',
                border: '2px solid var(--border)',
                transition: 'all 0.3s ease',
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s backwards`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.borderColor = 'var(--primary)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.borderColor = 'var(--border)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Logo Container */}
              <div style={{
                width: '120px',
                height: '120px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--bg)',
                borderRadius: '12px',
                padding: '15px',
                border: '1px solid var(--border)'
              }}>
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<div style="font-size: 48px;">🤝</div>';
                  }}
                />
              </div>

              {/* Partner Name */}
              <h3 style={{
                color: 'var(--text-accent)',
                fontSize: '24px',
                fontWeight: '700',
                marginBottom: '12px',
                wordBreak: 'break-word'
              }}>
                {partner.name}
              </h3>

              {/* Partner Description */}
              <p style={{
                color: 'var(--text-secondary)',
                fontSize: '15px',
                lineHeight: '1.6',
                marginBottom: '20px',
                flexGrow: 1
              }}>
                {partner.description}
              </p>

              {/* Partner Links */}
              {partner.links && partner.links.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  marginBottom: '10px',
                  width: '100%'
                }}>
                  {partner.links.map((link, linkIndex) => (
                    <a
                      key={linkIndex}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        backgroundColor: 'var(--bg)',
                        border: '1px solid var(--border)',
                        borderRadius: '8px',
                        color: 'var(--primary)',
                        fontSize: '14px',
                        fontWeight: '600',
                        textDecoration: 'none',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--primary)';
                        e.currentTarget.style.color = 'white';
                        e.currentTarget.style.transform = 'scale(1.02)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bg)';
                        e.currentTarget.style.color = 'var(--primary)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      {link.title}
                      <span style={{ fontSize: '12px' }}>→</span>
                    </a>
                  ))}
                </div>
              )}

              {/* Decorative Corner */}
              <div style={{
                position: 'absolute',
                top: '-2px',
                right: '-2px',
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
                opacity: 0.1
              }} />
            </div>
          ))}
        </div>
      )}

      {/* Call to Action for Partnership */}
      <div style={{
        marginTop: '80px',
        textAlign: 'center',
        padding: '50px 30px',
        backgroundColor: 'var(--card-bg)',
        borderRadius: '16px',
        border: '2px solid var(--primary)',
        background: 'linear-gradient(135deg, rgba(138, 43, 226, 0.05), rgba(0, 191, 255, 0.05))',
        animation: 'fadeInUp 1s ease-out'
      }}>
        <h2 style={{
          color: 'var(--text-accent)',
          fontSize: '32px',
          fontWeight: '700',
          marginBottom: '16px'
        }}>
          Interested in Partnering?
        </h2>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '16px',
          marginBottom: '30px',
          maxWidth: '600px',
          margin: '0 auto 30px'
        }}>
          We're always looking for partnerships that benefit our gaming community. 
          Get in touch to explore collaboration opportunities.
        </p>
        <a 
          href="/contact"
          style={{
            display: 'inline-block',
            padding: '14px 32px',
            background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
            color: 'white',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            textDecoration: 'none',
            transition: 'all 0.3s ease',
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(138, 43, 226, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          Contact Us
        </a>
      </div>
    </div>
  );
};

export default Partners;
